import { AfterViewInit, Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
// rxjs
import { select, Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

// Misc
import { env } from '@amfam/environment';
import { PolicySelectors, PolicySummaryActions } from '@amfam/policy/data-access';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
// Models
import { ConfirmationModel, LoadingSpinnerService, PageTitleService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import * as coverageChangeSelector from '../../+state';
import { SendCoverageAnalyticsAction } from '../../+state/coverage-change.actions';

@Component({
  selector: 'ds-coverage-change-confirmation',
  templateUrl: './coverage-change-confirmation.component.html',
  styleUrls: [
    './coverage-change-confirmation.component.scss',
    '../../../billing/shared/bill-accounts.scss'
  ],
  standalone: false
})
export class CoverageChangeConfirmationComponent implements OnInit, AfterViewInit {
  @ViewChild('headerCellTpl', { static: true }) headerCellTpl: TemplateRef<any>;
  @ViewChild('vehicleCellTpl', { static: true }) vehicleCellTpl: TemplateRef<any>;
  @ViewChild('newDeductibleCellTpl', { static: true }) newDeductibleCellTpl: TemplateRef<any>;
  @ViewChild('addonsCellTpl', { static: true }) addonsCellTpl: TemplateRef<any>;

  @Input() collisionCoverage: number;
  @Input() comprehensiveCoverage: number;
  @Input() eraAddon: boolean;
  @Input() rentalAddon: number;

  vehicleId: string;
  confirmationData$: Observable<ConfirmationModel>;

  // Table vars
  cssClasses = 'material noshadow expandable payment-method';

  currentVehicle$: Observable<string>;

  collisionCoverageValue$: Observable<string> = this.store.select(
    coverageChangeSelector.getSelectedCoverageLimitsByCode,
    'PACollisionCov'
  );
  rentalReimbursementValue$: Observable<string> = this.store.select(
    coverageChangeSelector.getSelectedCoverageLimitsByCode,
    'PARentalCov'
  );
  comprehensiveCoverageValue$: Observable<string> = this.store.select(
    coverageChangeSelector.getSelectedCoverageLimitsByCode,
    'PAComprehensiveCov'
  );

  ersValue$: Observable<string | boolean> = this.store.select(
    coverageChangeSelector.getSelectedERSCoverage,
    'PAEmergencyRoadServiceCov_af'
  );

  public advancedAutoRisks$ = this.store.pipe(
    select(
      fromRoot.getCoverageChangeRisks,
      this.featureService.isEnabled('kyd_model_coverage') ? [] : env.modelCoverageDisabledStates
    )
  );

  private coverageRequestType: 'OnlineBind' | 'SendToAgent';

  constructor(
    private store: Store<fromRoot.RootState>,
    private pageTitle: PageTitleService,
    private route: ActivatedRoute,
    private featureService: FeatureFlagService,
    private spinner: LoadingSpinnerService
  ) {}

  ngOnInit() {
    this.spinner.stop();
    this.currentVehicle$ = combineLatest(
      this.store.select(coverageChangeSelector.getSelectedVehicleIdentifier),
      this.store.select(PolicySelectors.selectAdvanceAutoPolicies),
      this.store.select(PolicySelectors.selectAdvanceAutoPolicyRisks),
      this.store.select(coverageChangeSelector.getCoverageRequestType),
      (vehicleId, policies, risks, coverageRequestType) => ({
        vehicleId: vehicleId,
        policies: policies,
        vehicles: risks,
        coverageRequestType: coverageRequestType
      })
    ).pipe(
      take(1),
      map(state => {
        this.vehicleId = state.vehicleId;
        this.coverageRequestType = state.coverageRequestType;
        const matchingVehicle = state.vehicles.find(
          vehicle => _get(vehicle, 'id') === this.vehicleId
        );
        if (this.coverageRequestType === 'OnlineBind') {
          this.pageTitle.set(`Update ${matchingVehicle.policyNumber} Changes Saved`);
        } else {
          this.pageTitle.set(`Changes to ${matchingVehicle.policyNumber} Sent to Agent`);
        }
        return matchingVehicle.year + ' ' + matchingVehicle.make + ' ' + matchingVehicle.model;
      })
    );
    this.confirmationData$ = this.buildConfirmationData(
      this.store.select(coverageChangeSelector.getCoverageRequestType)
    );
  }

  ngAfterViewInit() {
    this.store.dispatch(
      new SendCoverageAnalyticsAction({
        pageAnalyticName: this.coverageRequestType === 'OnlineBind' ? ':Complete' : ':SendToAgent'
      })
    );
  }
  routeToPolicies() {
    this.store.dispatch(PolicySummaryActions.LoadSummaries());
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/policies']
      })
    );
  }

  routeToRiskSelection() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/policies/coverage-change']
      })
    );
  }
  buildConfirmationData(coverageType: Observable<string>) {
    return combineLatest(
      coverageType,
      this.comprehensiveCoverageValue$,
      this.collisionCoverageValue$,
      this.ersValue$,
      this.rentalReimbursementValue$,
      (coverageTypeValue, comprehensiveValue, collisonValue, ersValue, rrvalue) => ({
        coverageTypeValue,
        comprehensiveValue,
        collisonValue,
        ersValue,
        rrvalue
      })
    ).pipe(
      map(res => ({
        confirmation: {
          heading: this.headingText(res.coverageTypeValue),
          infoText: this.getInfoText(res.coverageTypeValue)
        },
        details: [
          {
            heading: 'Details',
            tableColumns: this.buildColumnHeaders(res),
            tableRows: this.buildRowData()
          }
        ]
      }))
    );
  }

  private buildColumnHeaders(payload) {
    const columnArr: Array<any> = [
      {
        cellTemplate: this.vehicleCellTpl,
        headerTemplate: this.headerCellTpl,
        name: 'Vehicle',
        prop: 'vehicleName',
        draggable: false,
        resizeable: true,
        canAutoResize: true,
        width: 160
      }
    ];
    if (payload.comprehensiveValue || payload.collisonValue) {
      columnArr.push({
        cellTemplate: this.newDeductibleCellTpl,
        headerTemplate: this.headerCellTpl,
        name: 'Coverage Changes',
        shortName: 'New Deductibles',
        prop: 'newDeductibles',
        draggable: false,
        resizeable: true,
        canAutoResize: true,
        width: 200
      });
    }
    if (payload.ersValue || payload.rrvalue) {
      columnArr.push({
        cellTemplate: this.addonsCellTpl,
        headerTemplate: this.headerCellTpl,
        name: 'Additional Coverages',
        shortName: 'Add ons',
        prop: 'addons',
        draggable: false,
        resizeable: true,
        canAutoResize: true,
        width: 250
      });
    }

    return columnArr;
  }

  private buildRowData() {
    return [
      {
        vehicleName: this.currentVehicle$,
        newDeductibles: {
          comprehensive: this.comprehensiveCoverageValue$,
          collision: this.collisionCoverageValue$
        },
        addons: {
          rental: this.rentalReimbursementValue$,
          era: this.ersValue$
        }
      }
    ];
  }

  private headingText(coverageType: string) {
    if (coverageType === 'OnlineBind') {
      return 'Your policy has been updated.';
    }
    return 'We have sent your requested changes to your agent for review.';
  }

  private getInfoText(coverageType: string) {
    if (coverageType === 'OnlineBind') {
      return 'Your new coverage will be effective tomorrow.';
    }
    return `Once they have received the request, your agent will connect with you to discuss the changes.`;
  }
}
